import type { WidgetButtonStyle } from "@/types/ui/branding";

export type CtaButtonShape = "rounded" | "square" | "pill";

const RADIUS_MAP: Record<CtaButtonShape, string> = {
  pill: "24px",
  rounded: "10px",
  square: "4px",
};

export function ctaBorderRadius(shape: CtaButtonShape | undefined): string {
  return RADIUS_MAP[shape ?? "pill"];
}

interface ResolveCtaColorsArgs {
  brandColor: string;
  gradientEnd: string;
  connected: boolean;
  gradient: boolean;
  colorOverride?: WidgetButtonStyle["color"];
  colorOverride2?: WidgetButtonStyle["color2"];
  textColorOverride?: WidgetButtonStyle["textColor"];
}

export interface ResolvedCtaColors {
  start: string;
  end: string;
  background: string;
  color: string;
}

/** Shared color/background resolution for the Start Conversation CTA, used by
 *  both the live widget (VoiceAgentControls) and the admin-side WidgetPreview
 *  so the two can never visually drift. */
export function resolveCtaColors({
  brandColor,
  gradientEnd,
  connected,
  gradient,
  colorOverride,
  colorOverride2,
  textColorOverride,
}: ResolveCtaColorsArgs): ResolvedCtaColors {
  const hasCustomColor = !!colorOverride;
  const start = colorOverride || brandColor;
  const end = colorOverride2 || gradientEnd;
  // An explicit color pick is a deliberate choice to fill the button solid (or
  // as a 2-color gradient when colorOverride2 is also set) — it should render
  // that way regardless of the `gradient`/`connected` flags, which otherwise
  // only style the *default*, un-customized brand-color pill as a faint tint.
  const useGradientFill = hasCustomColor ? !!colorOverride2 : gradient && !connected;

  const background = useGradientFill
    ? `linear-gradient(90deg, ${start} 0%, ${end} 100%)`
    : connected || hasCustomColor
      ? start
      : `${start}10`;

  const color =
    textColorOverride || (useGradientFill || connected || hasCustomColor ? "#FFFFFF" : start);

  return { start, end, background, color };
}
